/**
 * Cookie / tracking consent (GDPR-style categories) for SaaS tenants.
 * Stored per browser on each customer's origin (localStorage).
 */

export const HM_COOKIE_CONSENT_KEY = 'hm_cookie_consent_v1';
export const CONSENT_SCHEMA_VERSION = 1;
export const HM_CONSENT_UPDATED_EVENT = 'hm-consent-updated';
export const HM_OPEN_COOKIE_PREFERENCES_EVENT = 'hm-open-cookie-preferences';

/**
 * @typedef {{
 *   v: number;
 *   decided: boolean;
 *   necessary: true;
 *   analytics: boolean;
 *   marketing: boolean;
 *   ts: string;
 * }} StoredCookieConsent
 */

/** @returns {StoredCookieConsent | null} */
export function readCookieConsent() {
    if (typeof window === 'undefined') return null;
    try {
        const raw = localStorage.getItem(HM_COOKIE_CONSENT_KEY);
        if (!raw) return null;
        const o = JSON.parse(raw);
        if (!o || typeof o !== 'object' || o.v !== CONSENT_SCHEMA_VERSION || !o.decided) return null;
        return {
            v: CONSENT_SCHEMA_VERSION,
            decided: true,
            necessary: true,
            analytics: !!o.analytics,
            marketing: !!o.marketing,
            ts: typeof o.ts === 'string' ? o.ts : new Date().toISOString(),
        };
    } catch {
        return null;
    }
}

/** @param {{ analytics: boolean; marketing: boolean }} prefs */
export function persistCookieConsent(prefs) {
    const data = {
        v: CONSENT_SCHEMA_VERSION,
        decided: true,
        necessary: true,
        analytics: !!prefs.analytics,
        marketing: !!prefs.marketing,
        ts: new Date().toISOString(),
    };
    try {
        localStorage.setItem(HM_COOKIE_CONSENT_KEY, JSON.stringify(data));
    } catch { /* quota / private mode */ }
    try {
        window.dispatchEvent(new CustomEvent(HM_CONSENT_UPDATED_EVENT, { detail: data }));
    } catch { /* noop */ }
}

export function consentAllowsAnalytics() {
    const c = readCookieConsent();
    return !!(c && c.analytics);
}

export function consentAllowsMarketing() {
    const c = readCookieConsent();
    return !!(c && c.marketing);
}

/** User completed a choice (accept / reject / save). */
export function hasCookieConsentBeenDecided() {
    return readCookieConsent() != null;
}

/** Google Consent Mode: downgrade without removing gtag (stops measurement use of storage). */
export function revokeGoogleTagConsent() {
    if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
    try {
        window.gtag('consent', 'update', {
            analytics_storage: 'denied',
            ad_storage: 'denied',
            ad_user_data: 'denied',
            ad_personalization: 'denied',
        });
    } catch { /* noop */ }
}

/** Meta: best-effort revoke; some builds support consent API. */
export function revokeMetaPixelConsent() {
    if (typeof window === 'undefined' || typeof window.fbq !== 'function') return;
    try {
        window.fbq('consent', 'revoke');
    } catch {
        /* fall through */
    }
}

export function dispatchOpenCookiePreferences() {
    try {
        window.dispatchEvent(new CustomEvent(HM_OPEN_COOKIE_PREFERENCES_EVENT));
    } catch { /* noop */ }
}
